// Process image export — WYSIWYG SVG/PNG of the process view as currently
// shown. Pure core: the same engine walk + scene renderers the live views
// (and the MCP/server exports) run on, fed with the CURRENT walk so branch
// picks, expanded subprocesses and dimmed alternatives match the screen.
//
// PNG is produced in-browser from the portable SVG (blob URL → Image →
// canvas). The exported SVG is self-contained (inline styles, no
// foreignObject), so the canvas path needs no DOM capture.
import {
  bpmnLayout,
  buildScaffold,
  swimlaneLayout,
  type SwimlaneContext,
  type WalkResult,
} from "@archlang/engine";
import { renderProcessSvg, renderSwimlaneSvg } from "@archlang/scene/process";
import { bpmnOwnerColor, swimlaneExportOptions } from "@archlang/viewer-core";
import { triggerDownload } from "../board/exportShared";
import type { OwnerColorFn } from "./ownerColorFor.js";

export type ProcessImageVariant = "flow" | "sequence" | "bpmn";

export interface BuildProcessImageArgs {
  readonly variant: ProcessImageVariant;
  /** The walk the live view renders — all-branches with the CURRENT walker
   *  state (selection dims, expansions inline), so the image matches the
   *  screen. */
  readonly walk: WalkResult;
  readonly swimlaneContext: SwimlaneContext;
  /** The app's owner-path coloring (the "colour by" picker). BPMN owner
   *  chips/strokes use it exactly like the live view; absent (or returning
   *  undefined for a path) falls back to the palette's value hash, so the
   *  label still reads as the colour the board gives it. */
  readonly ownerColor?: OwnerColorFn;
}

/** Compose the process image as a portable SVG string. */
export function buildProcessImageSvg(args: BuildProcessImageArgs): string {
  const { variant, walk, swimlaneContext, ownerColor } = args;
  if (variant === "bpmn") {
    const layout = bpmnLayout(buildScaffold(walk, swimlaneContext));
    // The rule (app picker → palette hash → chrome for actors) is viewer-core's.
    // Restating it here is what let a second palette back into the export once.
    return renderProcessSvg(layout, { ownerColor: bpmnOwnerColor(ownerColor) });
  }
  const layout = swimlaneLayout(walk, swimlaneContext, { variant });
  return renderSwimlaneSvg(layout, swimlaneExportOptions(walk, layout));
}

/** `Checkout` + `flow` + `svg` → `Checkout.flow.svg` (filesystem-safe). */
export function processImageFilename(
  processName: string,
  variant: ProcessImageVariant,
  format: "svg" | "png",
): string {
  const slug =
    processName.trim().replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") ||
    "process";
  return `${slug}.${variant}.${format}`;
}

export function downloadProcessSvg(svg: string, filename: string): void {
  triggerDownload(new Blob([svg], { type: "image/svg+xml" }), filename);
}

/** Root `width`/`height` attrs of a renderer-produced SVG. */
function svgSize(svg: string): { width: number; height: number } {
  const w = /\bwidth="(\d+(?:\.\d+)?)"/.exec(svg);
  const h = /\bheight="(\d+(?:\.\d+)?)"/.exec(svg);
  return {
    width: Math.max(1, Math.round(Number(w?.[1] ?? 800))),
    height: Math.max(1, Math.round(Number(h?.[1] ?? 600))),
  };
}

/** Rasterize the portable SVG in-browser (Image + canvas; no DOM capture). */
export async function svgToPngBlob(svg: string, scale = 2): Promise<Blob> {
  const { width, height } = svgSize(svg);
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    img.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Couldn't rasterize the diagram."));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("canvas produced no blob"))),
        "image/png",
      ),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadProcessPng(
  svg: string,
  filename: string,
  scale = 2,
): Promise<void> {
  triggerDownload(await svgToPngBlob(svg, scale), filename);
}
